// The posting strategy on the requisition (G §5 "Posting strategy preserves original/current/reason.", "Original posting
// type + current posting strategy."; DS Q-12, DC-71): the request's posting type stays the original, the current
// strategy beside it, and every PostingStrategyVersion newest first with who, when and why (Versions row 119). The
// Coordinator's posting edits after it went up (REQ ID, channels) are their own dialog with the reason
// (`requisition.post_update`, review row F10): "Posting strategy changes require reason/audit."
// The Recruiter's *Change* (`requisition.update_strategy`) chooses from the Admin's published
// `taxonomies:posting_strategy` list, read through `config.options`; with no list published there is nothing to choose,
// and the dialog says so through the refusal rather than offering a list the screen would invent.

import { useState } from "react";
import { configOptions, requisitionPostUpdate, requisitionUpdateStrategy, type RequisitionFull } from "@/api/tools";
import { useWrite } from "@/api/useTool";
import {
  ACCESS_LABELS, ACTION_LABELS, Button, Card, CheckTile, DIALOG_LABELS, Dialog, Field, G1_LABELS, Input, JOURNEY_LABELS, KeyValue, MILESTONE_LABELS,
  Paragraph, REQUISITION_STATUS, Select, Stack, TextArea, VERSION_LABELS, Versions, type VersionRow,
} from "@/mc";
import { requisitionReads, useSettledRead } from "./data";
import { optionsOf } from "./StateChange";
import { REQ_LABELS, REQ_RULES } from "./labels";

type Channel = "internal" | "external";

/** The latest version number on either track: what `expected_version` names on the two update tools (0 for none). */
export function latestVersionNo(r: RequisitionFull): number {
  return Math.max(0, ...(r.posting_strategy?.history ?? []).map((version) => version.version_no));
}

/** A version's author by the seats the record names (the version carries the id only). */
function authorOf(r: RequisitionFull, id: string): string {
  const seats: Record<string, string | null | undefined> = {
    [r.hiring_manager_id]: r.hiring_manager_name,
    [r.hrbp_id]: r.hrbp_name,
    ...(r.recruiter_id ? { [r.recruiter_id]: r.recruiter_name } : {}),
    ...(r.coordinator_id ? { [r.coordinator_id]: r.coordinator_name } : {}),
  };
  return seats[id] ?? "—";
}

export function PostingStrategyCard({ r, onUpdate, onChange }: {
  r: RequisitionFull; onUpdate?: (() => void) | undefined; onChange?: (() => void) | undefined;
}) {
  const view = r.posting_strategy;
  const current = view?.current ?? null;
  const rows: VersionRow[] = (view?.history ?? []).map((version) => ({
    n: version.version_no,
    at: version.effective_at,
    actor: authorOf(r, version.author_id),
    actorKey: version.author_id,
    reason: version.reason ?? (version.track === "posting" ? REQUISITION_STATUS.posted : version.strategy_label ?? "—"),
  }));
  return (
    <Card
      title={REQ_LABELS.postingStrategy}
      actions={(r.can.update_strategy && onChange) || (r.can.post_update && onUpdate) ? (
        <>
          {r.can.update_strategy && onChange ? <Button sm onClick={onChange} >{JOURNEY_LABELS.change}</Button> : null}
          {r.can.post_update && onUpdate ? <Button sm onClick={onUpdate} >{ACCESS_LABELS.Edit}</Button> : null}
        </>
      ) : undefined}
    >
      <Stack gap={12}>
        <KeyValue
          rows
          items={[
            [REQ_LABELS.originalPostingType, r.original_posting_type_label ?? "—"],
            [VERSION_LABELS.currentPostingStrategy, current?.strategy_label ?? "—"],
            [REQ_LABELS.reqId, r.req_no ?? "—"],
            [MILESTONE_LABELS.posting, r.posting_channels?.length ? r.posting_channels.map((c) => (c === "internal" ? G1_LABELS.internal : G1_LABELS.external)).join(" · ") : "—"],
          ]}
        />
        <Paragraph sm>{REQ_RULES.preservePostingStrategy}</Paragraph>
        {rows.length ? <Versions label={REQ_LABELS.postingStrategy} rows={rows} /> : null}
      </Stack>
    </Card>
  );
}

/** The Coordinator's posting update after it went up: REQ ID, channels and the reason, as a new posting version. */
export function PostUpdateDialog({ r, open, onClose }: { r: RequisitionFull; open: boolean; onClose: () => void }) {
  const update = useWrite(
    (input: { requisition_id: string; expected_version: number; req_no: string | null; posting_channels: Channel[] | null; reason: string }, key) => requisitionPostUpdate(input, key),
    requisitionReads(r.id),
  );
  const [reqNo, setReqNo] = useState(r.req_no ?? "");
  const [channels, setChannels] = useState<Channel[]>((r.posting_channels ?? []).filter((c): c is Channel => c === "internal" || c === "external"));
  const [reason, setReason] = useState("");
  const toggle = (channel: Channel, on: boolean) => setChannels((prev) => (on ? [...prev.filter((c) => c !== channel), channel] : prev.filter((c) => c !== channel)));
  const submit = () => void update.mutateAsync({
    requisition_id: r.id,
    expected_version: latestVersionNo(r),
    req_no: reqNo.trim() && reqNo.trim() !== r.req_no ? reqNo.trim() : null,
    posting_channels: r.confidential ? null : channels,
    reason: reason.trim(),
  }).then(onClose).catch(() => undefined);
  const fields = update.error?.fieldErrors ?? {};
  return (
    <Dialog
      title={REQ_LABELS.postingStrategy}
      open={open}
      onClose={onClose}
      dismissLabel={DIALOG_LABELS.cancel}
      primary={{ label: ACTION_LABELS.save, onClick: submit, busy: update.isPending, hs: "req.post-update" }}
      refused={update.error && !fields.reason && !fields.req_no ? { reason: update.error.remedy || update.error.message } : undefined}
      
    >
      <Stack gap={16}>
        <Field label={REQ_LABELS.reqId} error={fields.req_no}>
          <Input value={reqNo} onChange={(event) => setReqNo(event.target.value)} mono  />
        </Field>
        {!r.confidential ? (
          <div role="group" aria-label={MILESTONE_LABELS.posting}>
            <Stack gap={8}>
              <CheckTile checked={channels.includes("internal")} onChange={(on) => toggle("internal", on)} >{G1_LABELS.internal}</CheckTile>
              <CheckTile checked={channels.includes("external")} onChange={(on) => toggle("external", on)} >{G1_LABELS.external}</CheckTile>
            </Stack>
          </div>
        ) : null}
        <Field label={G1_LABELS.reason} required error={fields.reason} hint={G1_LABELS.reasonStored}>
          <TextArea value={reason} onChange={(event) => setReason(event.target.value)}  />
        </Field>
      </Stack>
    </Dialog>
  );
}

/** The Recruiter's strategy change: a choice of the published list and the reason, as a new strategy version. */
export function UpdateStrategyDialog({ r, open, onClose }: { r: RequisitionFull; open: boolean; onClose: () => void }) {
  const change = useWrite(
    (input: { requisition_id: string; expected_version: number; strategy: string; reason: string }, key) => requisitionUpdateStrategy(input, key),
    requisitionReads(r.id),
  );
  const list = useSettledRead(
    ["config-options", "taxonomies", "posting_strategy"],
    () => configOptions({ kind: "taxonomies", name: "posting_strategy" }),
    { enabled: open, staleTime: 5 * 60_000 },
  );
  const [strategy, setStrategy] = useState(r.posting_strategy?.current?.strategy ?? "");
  const [reason, setReason] = useState("");
  const submit = () => void change.mutateAsync({
    requisition_id: r.id,
    expected_version: latestVersionNo(r),
    strategy,
    reason: reason.trim(),
  }).then(onClose).catch(() => undefined);
  const fields = change.error?.fieldErrors ?? {};
  return (
    <Dialog
      title={REQ_LABELS.postingStrategy}
      open={open}
      onClose={onClose}
      dismissLabel={DIALOG_LABELS.cancel}
      primary={{ label: ACTION_LABELS.save, onClick: submit, busy: change.isPending, hs: "req.strategy-save" }}
      refused={change.error && !fields.reason && !fields.strategy ? { reason: change.error.remedy || change.error.message } : undefined}
      
    >
      <Stack gap={16}>
        <Field label={REQ_LABELS.postingStrategy} required error={fields.strategy}>
          <Select options={optionsOf(list.data)} value={strategy} onChange={setStrategy}  />
        </Field>
        <Field label={G1_LABELS.reason} required error={fields.reason} hint={G1_LABELS.reasonStored}>
          <TextArea value={reason} onChange={(event) => setReason(event.target.value)}  />
        </Field>
        <Paragraph sm>{REQ_RULES.preservePostingStrategy}</Paragraph>
      </Stack>
    </Dialog>
  );
}
